<template>
  <div
    v-if="product"
    class="product-addons-drawer"
  >
    <div class="product-addons-drawer__header">
      <h2
        class="text-body-m-regular"
        v-text="$string('product.add_ons.drawer.added_to_bag')"
      />

      <button
        class="product-addons-drawer__close button--reset"
        type="button"
        @click.prevent="handleClose"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.close')"
        />

        <icon-close />
      </button>
    </div>

    <div class="product-addons-drawer__added-pendant">
      <responsive-image
        :image="product.featuredImage"
        sizes="70px"
        min-max="70-140"
      />

      <div class="flex flex--col flex--justify-center text-left">
        <h3
          class="text-body-s-regular text-body-m-regular-desktop"
          v-text="product.title"
        />

        <p
          class="mt-xs text-body-s-regular product-addons-drawer__added-note"
          v-text="$string('product.add_ons.drawer.added_note')"
        />
      </div>
    </div>

    <div class="mt-xs flex product-addons-drawer__title product-addons-drawer--gray-text">
      <h2
        class="text-body-m-medium text-body-l-medium-desktop"
        v-text="title"
      />

      <span
        class="text-body-m-medium"
        :aria-label="`Viewing product ${activeProductIndex + 1} of ${products.length}`"
      >
        {{ activeProductIndex + 1 }}/{{ products.length }}
      </span>
    </div>

    <splide
      ref="splide"
      :options="splideOptions"
      :has-track="false"
      @splide:active="(slide) => (activeProductIndex = slide.index)"
    >
      <splide-track>
        <splide-slide
          v-for="addon of products"
          :key="addon.id"
          class="product-addons-drawer__product"
        >
          <responsive-image
            :image="addon.featuredImage"
            sizes="376px"
            min-max="287-752"
          />

          <h3>
            <a
              class="text-body-m-regular text-body-l-regular-desktop"
              :href="addon.url"
              v-text="addon.title"
            />
          </h3>

          <product-prices
            :price="addon.price.amount"
            :compare-at-price="false"
            class-price="text-body-m-regular text-body-l-regular-desktop"
          />
        </splide-slide>
      </splide-track>

      <div class="splide__arrows splide__arrows--ltr">
        <button
          type="button"
          class="splide__arrow splide__arrow--prev"
          :aria-label="$string('accessibility.carousel.previous')"
        >
          <icon-arrow-left />
        </button>

        <button
          type="button"
          class="splide__arrow splide__arrow--next"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <icon-arrow-right />
        </button>
      </div>
    </splide>

    <dl
      v-if="getMetal()?.value"
      class="flex text-body-s-regular"
    >
      <dt
        class="product-addons-drawer--gray-text"
        v-text="getMetal().label"
      />
      <dd v-text="`: ${getMetal().value}`" />
    </dl>

    <product-options
      v-if="selectedProduct"
      :product="selectedProduct"
      :variant="variant"
      :display-missing-options="displayMissingOptions"
      @update-variant="(newVariant) => (variant = newVariant)"
    >
      <template #sizeGuide>
        <size-guide-btn :product="selectedProduct" />
      </template>
    </product-options>

    <div>
      <btn
        :label="$string('product.cta.add_to_cart')"
        :disabled="!selectedProduct"
        :show-state-icon="loading"
        @click="handleAddToCart"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </div>

    <div class="flex flex--justify-center">
      <btn
        class="flex"
        :url="$variable('routes.cart.url')"
        :label="$string('product.add_ons.drawer.view_shopping_bag')"
        modifiers="text"
        @click="track({ event: 'pdp_addons_drawer_view_shopping_bag' })"
      />
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product addons drawer (product-addons-drawer)
 * -----------------------------------------------------------------------------
 * Display additional products in a drawer after the shopper adds the paired item
 * to the cart without one of the addon products
 *
 */

import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'

import ProductOptions from '~async/product-form/product-options'
import SizeGuideBtn from '~async/size-guide-btn/size-guide-btn'

import productsQuery from '~graphql/products.gql'

import { openCart } from '~/helpers/cart'
import { formatEcommerceEvent, track } from '~/helpers/datalayer-actions'
import { formatProduct } from '~/helpers/format'
import { getMetafield } from '~/helpers/general'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { getAddOns, getLineItemProperties, isAddOnInCart } from '~/helpers/product'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconLoading from '~icons/general/loading.svg'

export default {
  name: 'ProductAddonsDrawer',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    IconClose,
    IconLoading,
    ProductOptions,
    ProductPrices,
    ResponsiveImage,
    SizeGuideBtn,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      queriedAddOnProducts: [],
      activeProductIndex: 0,
      variant: false,
      displayMissingOptions: false,
      loading: false,
      splideOptions: {
        flickMaxPages: 1,
        perMove: 1,
        perPage: 1,
        wheel: false,
        arrows: true,
        drag: true,
        pagination: false,
        flickPower: 200,
        type: 'fade',
        omitEnd: true,
      },
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartItems: (state) => state.cart.response.items,
      lastAddedItem: (state) => state.cart.lastAddedItem,
    }),

    /**
     * Get add on metaobject from product metafields
     * @returns {Object}
     */
    metaobject() {
      return getAddOns(this.product)
    },

    /**
     * Compute add on products
     * @returns {Array}
     */
    products() {
      if (!this.metaobject) {
        return []
      }

      if (!this.product?.liquid) {
        return this.queriedAddOnProducts
      }

      return this.metaobject.products?.filter((product) => {
        return product?.available
      })
    },

    /**
     * Compute main accordion title
     * @returns {String}
     */
    title() {
      return this.metaobject?.title || this.$string('product.add_ons.title')
    },

    /**
     * Compute if addon is in cart
     */
    isAddOnInCart() {
      return isAddOnInCart(this.product, this.cartItems)
    },

    /**
     * Compute selected product (the active slide)
     */
    selectedProduct() {
      return this.products[this.activeProductIndex] ?? false
    },
  },

  watch: {
    /**
     * Watch for changes in metaobject to query products.
     * Products should be queried if the products array is a list of GIDs
     */
    metaobject(meta, oldMeta) {
      const products = meta?.products || []
      const oldProducts = oldMeta?.products || []
      if (
        this.product.liquid ||
        typeof products[0] !== 'string' ||
        !products[0]?.includes('gid') ||
        JSON.stringify(products) === JSON.stringify(oldProducts)
      ) {
        return
      }
      this.queryProducts()
    },

    /**
     * Open addon drawer if the user added an item to the cart without an addon item
     */
    lastAddedItem() {
      if (
        this.product &&
        this.lastAddedItem?.product_id === this.product?.id &&
        !this.isAddOnInCart
      ) {
        this.overlay.open({ ignoreDismissed: true })
        this.overlay.update()
        track({ event: 'pdp_addons_drawer_open' })
      }
    },

    /**
     * Update tabbable elements when the user changes which addon product they're viewing
     */
    activeProductIndex() {
      if (this.overlay.isActive) {
        this.overlay.update()
      }
      this.variant = false
      this.displayMissingOptions = false
      track({
        event: 'pdp_addons_drawer_product_view',
        ...formatEcommerceEvent({
          product: this.selectedProduct,
        }),
      })
    },

    /**
     * Refresh splide because arrows are sometimes disabled when the overlay is opened.
     */
    async 'overlay.isActive'(isActive) {
      if (!isActive) {
        return
      }
      await this.$nextTick()
      this.$refs.splide.splide.refresh()
    },

    /**
     * Reset active product index if products array changes
     */
    products() {
      this.activeProductIndex = 0
    },
  },

  created() {
    if (this.product?.liquid) {
      return
    }
    this.queryProducts()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Fetch products using GIDs
     */
    async queryProducts() {
      const ids = this.metaobject?.products || []
      if (!ids.length) {
        return
      }

      try {
        this.queriedAddOnProducts = []

        const response = await cnvs.Query({
          query: productsQuery,
          variables: getVariables({
            ids,
            metafieldIds: [...getMetafieldIDs('product'), ...getMetafieldIDs('variant')],
          }),
        })

        this.queriedAddOnProducts = response.nodes
          .map((product) => {
            return formatProduct({ product })
          })
          .filter((product) => {
            return product?.available
          })
      } catch (error) {
        cnvs.ReportError('[Product Add On - Product Query]', error)
      }
    },

    /**
     * Get metal metafield
     */
    getMetal() {
      return getMetafield({
        metafields: this.selectedProduct.metafields || [],
        namespace: 'product_details',
        key: 'metal',
      })
    },

    /**
     * Track add to cart
     */
    trackAddToCart() {
      if (!this.selectedProduct) {
        return
      }
      track({
        event: `pdp_addons_drawer_add_to_cart`,
        ...formatEcommerceEvent({
          product: this.selectedProduct,
          variant: this.variant,
        }),
      })
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.displayMissingOptions = true
        return
      }

      this.displayMissingOptions = false

      try {
        this.loading = true
        await this.addItem([
          {
            id: this.variant.id,
            properties: getLineItemProperties(this.variant, this.product),
            quantity: 1,
          },
        ])

        this.overlay.close()
        openCart(this.getComponentExists, this.openOverlay)
        this.trackAddToCart()
      } catch (error) {
        cnvs.ReportError('Failed to add product to cart', error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Close overlay on button click and track click
     */
    handleClose() {
      this.overlay.close('button')
      track({ event: 'pdp_addons_drawer_close' })
    },
  },
}
</script>

<style lang="scss">
@import './product-addons-drawer';
</style>
